import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {
  forksListPath,
  orgTopicReposPath,
  repoCommitActivityInsightsPath,
  repoIssuesPath,
  repoPullRequestsPath,
  repositoryParticipationPath,
  repositoryPath,
  repoStargazersPath,
} from '@github-ui/paths'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Icon} from '@primer/octicons-react'
import {
  GearIcon,
  GitPullRequestIcon,
  IssueOpenedIcon,
  LawIcon,
  RepoCloneIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  StarIcon,
} from '@primer/octicons-react'
import {Label, Link, RelativeTime, Tooltip, Truncate} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {memo, useRef, useState} from 'react'

import type {RepositoryItem} from '../types/repository-item'
import {LanguageCircle} from './LanguageCircle'
import styles from './ReposListItem.module.css'
import {SearchMatchText} from './SearchMatchText'
import {Sparkline} from './Sparkline'

interface ReposListItemProps {
  repo: RepositoryItem
  query: string
  showNameWithOwner: boolean
}

export const ReposListItem = memo(function ReposListItem(props: ReposListItemProps) {
  const {repo, showNameWithOwner} = props
  const {variant} = useListViewVariant()

  if (variant === 'default') {
    return (
      <ListItem title={showNameWithOwner ? <DefaultItemTitleWithOwner {...props} /> : <DefaultItemTitle {...props} />}>
        <DefaultItemContent {...props} />
      </ListItem>
    )
  }

  return (
    <ListItem
      metadata={<CompactItemMetadata repo={repo} />}
      title={showNameWithOwner ? <CompactItemTitleWithOwner {...props} /> : <CompactItemTitle {...props} />}
      className={styles.CompactListItem}
      metadataContainerClassName={styles.CompactItemMetadataContainer}
    >
      <ListItemLeadingContent className={styles.CompactItemLeadingContent}>
        <ListItemLeadingVisual icon={getRepoIcon(repo)} color="fg.muted" />
      </ListItemLeadingContent>
      <ListItemMainContent>
        <CompactWideItemContent {...props} />
        <CompactNarrowItemContent {...props} />
      </ListItemMainContent>
    </ListItem>
  )
})

function DefaultItemContent({repo, query}: {repo: RepositoryItem; query: string}) {
  const {description, lastUpdated, license, allTopics, isAdminableByUser} = repo
  const topicsProps = {topics: allTopics, owner: repo.owner, name: repo.name}

  return (
    <ListItemMainContent>
      <div className={styles.DefaultItemContent}>
        {description && (
          <ListItemDescription>
            <FormattedDescription description={description} query={query} className={styles.FormattedDescription} />
          </ListItemDescription>
        )}

        {allTopics.length > 0 && (
          <>
            <TopicsList {...topicsProps} max={3} className={styles.SmallScreenOnly} />
            <TopicsList {...topicsProps} max={10} className={styles.HideOnSmallScreen} />
          </>
        )}

        <div className={styles.LabelsContainer}>
          <LanguageLabel {...repo} />
          {repo.primaryLanguage && <span>•</span>}

          {license && (
            <>
              <LicenseLabel {...repo} />
              <span>•</span>
            </>
          )}

          <ForksLabel {...repo} />
          <span>•</span>

          <StarsLabel {...repo} />
          <span>•</span>

          {repo.issueCount !== null && (
            <>
              <IssuesLabel {...repo} issueCount={repo.issueCount} />
              <span>•</span>
            </>
          )}

          {repo.pullRequestCount !== null && (
            <>
              <PullRequestsLabel {...repo} pullRequestCount={repo.pullRequestCount} />
              <span>•</span>
            </>
          )}

          <span className={styles.LastUpdated}>
            {lastUpdated.hasBeenPushedTo ? 'Updated' : 'Created'} <RelativeTime datetime={lastUpdated.timestamp} />
          </span>

          <RelativeTime datetime={lastUpdated.timestamp} format="micro" className={styles.LastUpdatedNarrow} />

          {isAdminableByUser && (
            <>
              <span>•</span>
              <RepoSettingsLink {...repo} />
            </>
          )}
        </div>
      </div>
    </ListItemMainContent>
  )
}

function CompactNarrowItemContent({repo, query}: {repo: RepositoryItem; query: string}) {
  const {description, isAdminableByUser, pullRequestCount} = repo

  return (
    <div className={styles.CompactNarrowItemContent}>
      {description && (
        <ListItemDescription>
          <FormattedDescription description={description} query={query} className={styles.FormattedDescription} />
        </ListItemDescription>
      )}
      <div className={styles.LabelsContainer}>
        <LanguageLabel {...repo} />
        {repo.primaryLanguage && <span>•</span>}

        <ForksLabel {...repo} />
        <span>•</span>

        <StarsLabel {...repo} />
        <span>•</span>

        {pullRequestCount !== null && <PullRequestsLabel {...repo} pullRequestCount={pullRequestCount} />}

        {isAdminableByUser && (
          <>
            <span>•</span>
            <RepoSettingsLink {...repo} />
          </>
        )}
      </div>
    </div>
  )
}

function CompactWideItemContent({repo, query}: {repo: RepositoryItem; query: string}) {
  const {description} = repo
  return (
    <ListItemDescription className={styles.CompactWideItemDescription}>
      <FormattedDescription description={description} query={query} />
    </ListItemDescription>
  )
}

function DefaultItemTitle({repo, query}: {repo: RepositoryItem; query: string}) {
  const {name, type, owner} = repo

  return (
    <ListItemTitle
      value={<SearchMatchText query={query}>{name}</SearchMatchText>}
      href={repositoryPath({owner, repo: name})}
      headingClassName={styles.DefaultItemTitle}
      containerClassName={styles.DefaultItemTitleContainer}
    >
      <Label variant="secondary" data-listview-item-visibility-label>
        {type}
      </Label>
      <ParticipationSparkline repo={repo} />
    </ListItemTitle>
  )
}

function CompactItemTitle({repo, query}: {repo: RepositoryItem; query: string}) {
  const {name, type, owner} = repo

  return (
    <ListItemTitle
      value={<SearchMatchText query={query}>{name}</SearchMatchText>}
      href={repositoryPath({owner, repo: name})}
      headingClassName={styles.CompactItemTitle}
      containerClassName={styles.CompactItemTitleContainer}
    >
      <Label variant="secondary" className={styles.CompactItemTypeLabel}>
        {type}
      </Label>
    </ListItemTitle>
  )
}

function DefaultItemTitleWithOwner({repo, query}: {repo: RepositoryItem; query: string}) {
  const {name, type, owner} = repo
  const searchMatchName = <SearchMatchText query={query}>{name}</SearchMatchText>
  const searchMatchOwner = <SearchMatchText query={query}>{owner}</SearchMatchText>

  return (
    <ListItemTitle
      value={<NwoTitle name={searchMatchName} owner={searchMatchOwner} />}
      href={repositoryPath({owner, repo: name})}
      headingClassName={styles.DefaultItemTitle}
      containerClassName={styles.DefaultItemTitleContainer}
    >
      <Label variant="secondary">{type}</Label>
      <ParticipationSparkline repo={repo} />
    </ListItemTitle>
  )
}

function CompactItemTitleWithOwner({repo, query}: {repo: RepositoryItem; query: string}) {
  const {name, type, owner} = repo
  const searchMatchName = <SearchMatchText query={query}>{name}</SearchMatchText>
  const searchMatchOwner = <SearchMatchText query={query}>{owner}</SearchMatchText>

  return (
    <ListItemTitle
      value={<NwoTitle name={searchMatchName} owner={searchMatchOwner} />}
      href={repositoryPath({owner, repo: name})}
      headingClassName={styles.CompactItemTitle}
      containerClassName={styles.CompactItemTitleContainer}
    >
      <Label variant="secondary" className={styles.CompactItemTypeLabel}>
        {type}
      </Label>
    </ListItemTitle>
  )
}

function CompactItemMetadata({repo}: {repo: RepositoryItem}) {
  return (
    <>
      <ListItemMetadata className={styles.CompactItemLanguageLabel}>
        <LanguageLabel truncate {...repo} />
      </ListItemMetadata>
      <ListItemMetadata className={styles.CompactItemSettingsLink}>
        {repo.isAdminableByUser && <RepoSettingsLink {...repo} />}
      </ListItemMetadata>
      <ListItemMetadata className={styles.CompactItemAttributes}>
        <ForksLabel {...repo} />
      </ListItemMetadata>
      <ListItemMetadata className={styles.CompactItemAttributes}>
        <StarsLabel {...repo} />
      </ListItemMetadata>
    </>
  )
}

function TopicLabel({topic, owner}: {topic: string; owner: string}) {
  return (
    <Link key={topic} href={orgTopicReposPath({topic, org: owner})} data-testid={`${topic}-topic-link`}>
      <Label size="large" className={styles.TopicLabel}>
        {topic}
      </Label>
    </Link>
  )
}

interface TopicsListProps {
  topics: string[]
  owner: string
  max: number
  name: string
  className?: string
}

function TopicsList({topics, owner, max, name, className}: TopicsListProps) {
  const shownTopics = topics.slice(0, max)
  const numberOfTopicsNotShown = Math.max(0, topics.length - max)

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  return (
    <div className={clsx(styles.TopicsList, className)}>
      {shownTopics.map(topic => (
        <TopicLabel key={topic} topic={topic} owner={owner} />
      ))}
      {numberOfTopicsNotShown > 0 && (
        <Label
          as="button"
          aria-label={`Show ${numberOfTopicsNotShown} more ${pluralizeTopic(numberOfTopicsNotShown)}`}
          className={styles.MoreTopicsButton}
          size="large"
          variant="secondary"
          onClick={() => setIsDialogOpen(true)}
        >
          + {numberOfTopicsNotShown}
        </Label>
      )}
      {isDialogOpen && (
        <Dialog
          title={`Topics for ${owner}/${name}`}
          width="small"
          position={{narrow: 'bottom', wide: 'center'}}
          footerButtons={[{content: 'Done', onClick: () => setIsDialogOpen(false)}]}
          onClose={() => setIsDialogOpen(false)}
        >
          <div className={styles.TopicsList}>
            {topics.map(topic => (
              <TopicLabel key={topic} topic={topic} owner={owner} />
            ))}
          </div>
        </Dialog>
      )}
    </div>
  )
}

interface LanguageLabelProps {
  primaryLanguage: {name: string; color: string} | null
  truncate?: boolean
}

function LanguageLabel({primaryLanguage, truncate}: LanguageLabelProps) {
  if (!primaryLanguage) {
    return null
  }

  return (
    <div className={styles.LanguageLabelContainer}>
      <LanguageCircle color={primaryLanguage.color} />
      {truncate ? (
        <Truncate inline title={primaryLanguage.name} className={styles.TruncatedPrimaryLanguageName}>
          {primaryLanguage.name}
        </Truncate>
      ) : (
        <span className={styles.PrimaryLanguageName}>{primaryLanguage.name}</span>
      )}
    </div>
  )
}

function LicenseLabel({license}: {license: string | null}) {
  if (!license) {
    return null
  }

  return <IconLabel content={license} icon={LawIcon} />
}

function StarsLabel({starsCount, owner, name}: {starsCount: number; owner: string; name: string}) {
  const count = human(starsCount, {precision: getSignificanceBasedPrecision(starsCount)})
  const unit = count === '1' ? 'star' : 'stars'
  const label = `${count} ${unit}`
  return <IconLabel aria-label={label} content={count} icon={StarIcon} href={repoStargazersPath(owner, name)} />
}

function ForksLabel({forksCount, owner, name}: {forksCount: number; owner: string; name: string}) {
  const count = human(forksCount, {precision: getSignificanceBasedPrecision(forksCount)})
  const unit = count === '1' ? 'fork' : 'forks'
  const label = `${count} ${unit}`
  return (
    <IconLabel aria-label={label} content={count} icon={RepoForkedIcon} href={forksListPath({owner, repo: name})} />
  )
}

function IssuesLabel({issueCount, owner, name}: {issueCount: number; owner: string; name: string}) {
  const count = human(issueCount, {precision: getSignificanceBasedPrecision(issueCount)})
  const unit = count === '1' ? 'issue' : 'issues'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'issue-count'}
      content={count}
      icon={IssueOpenedIcon}
      href={repoIssuesPath(owner, name)}
    />
  )
}

function PullRequestsLabel({pullRequestCount, owner, name}: {pullRequestCount: number; owner: string; name: string}) {
  const count = human(pullRequestCount, {precision: getSignificanceBasedPrecision(pullRequestCount)})
  const unit = count === '1' ? 'pull request' : 'pull requests'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'pull-request-count'}
      content={count}
      icon={GitPullRequestIcon}
      href={repoPullRequestsPath(owner, name)}
    />
  )
}

interface IconLabelProps {
  icon: Icon
  href?: string
  content: string
  ['aria-label']?: string
  ['data-testid']?: string
}

function IconLabel({icon: IconComponent, href, content, ...props}: IconLabelProps) {
  if (href) {
    return (
      <Link {...props} muted href={href} className={styles.IconLabel}>
        <IconComponent />
        {content}
      </Link>
    )
  }

  return (
    <div {...props} className={styles.IconLabel}>
      <IconComponent />
      {content}
    </div>
  )
}

function ParticipationSparkline({repo}: {repo: RepositoryItem}) {
  const [participation, setParticipation] = useState(repo.participation)
  const isFetchingRef = useRef(false)

  async function fetchParticipation() {
    if (participation || isFetchingRef.current) return
    isFetchingRef.current = true

    const response = await verifiedFetchJSON(repositoryParticipationPath({owner: repo.owner, repo: repo.name}))
    if (response.ok) {
      const bodyText = await response.text()
      const json = bodyText.length > 0 ? JSON.parse(bodyText) : []
      setParticipation(json)
    }
  }

  const sentinelRef = useIntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) {
      fetchParticipation()
    }
  })

  return participation ? (
    participation.length === 0 ? (
      <div data-testid="empty-repo-sparkline" />
    ) : (
      <Tooltip text={`${repo.owner}/${repo.name}’s past year of commit activity`}>
        <div className={styles.SparklineContainer}>
          <Link
            className="d-block"
            href={repoCommitActivityInsightsPath({owner: repo.owner, repo: repo.name})}
            aria-label={`${repo.owner}/${repo.name}'s past year of commit activity`}
            target="_blank"
          >
            <div className={styles.HideOnSmallScreen}>
              <Sparkline uniqueKey={`${repo.owner}/${repo.name}`} points={participation} />
            </div>
            <div className={styles.SmallScreenOnly} data-listview-item-tiny-sparkline>
              <Sparkline variant="tiny" uniqueKey={`${repo.owner}/${repo.name}#tiny`} points={participation} />
            </div>
          </Link>
        </div>
      </Tooltip>
    )
  ) : (
    <div ref={sentinelRef} role="presentation" />
  )
}

interface FormattedDescriptionProps {
  description: string
  query: string
  className?: string
}

function FormattedDescription({description, query, className}: FormattedDescriptionProps) {
  return (
    // `repos-list-description` class is required to support `Link underlines` user setting
    <div className={clsx('repos-list-description width-full', className)}>
      <Truncate title={description} maxWidth="100%">
        <SearchMatchText query={query}>{description}</SearchMatchText>
      </Truncate>
    </div>
  )
}

function NwoTitle({name, owner}: {name: ReactNode; owner: ReactNode}) {
  return (
    <span className={styles.NwoTitle}>
      <span className={styles.RepoOwner}>{owner}/</span>
      <span>{name}</span>
    </span>
  )
}

function RepoSettingsLink({owner, name}: {owner: string; name: string}) {
  return (
    <Link muted href={`${repositoryPath({owner, repo: name})}/settings`} aria-label="Repository settings">
      <GearIcon />
    </Link>
  )
}

function getRepoIcon(repo: RepositoryItem) {
  const {isFork, type} = repo
  const lowerCaseType = type.toLowerCase()

  let icon
  if (isFork) {
    icon = RepoForkedIcon
  } else if (lowerCaseType.includes('mirror')) {
    icon = RepoCloneIcon
  } else if (lowerCaseType.includes('private') || lowerCaseType.includes('internal')) {
    icon = RepoLockedIcon
  } else {
    icon = RepoIcon
  }

  return icon
}

function pluralizeTopic(count: number) {
  return count === 1 ? 'topic' : 'topics'
}
